import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import axios from 'axios';
import { Storage } from 'aws-amplify';

import { TextField, Box, Tabs, Tab } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CloudUpload from '@material-ui/icons/CloudUpload';
import PrimaryButton from './lib/buttons/PrimaryButton';
import NeutralButton from './lib/buttons/NeutralButton';
import SecondaryButton from './lib/buttons/SecondaryButton';
import { Loading } from '../stories/Loading';
import PhoneLookup from './PhoneLookup';

import idPlaceholder from '../assets/images/id-placeholder.svg';

const { NODE_ENV } = process.env;
const isProduction = NODE_ENV === 'production';
const PHOTO_ID_BASE_URI = '/api/guestFile';

interface GuestRegisterProps {
  isPhoneLookedUp?: boolean;
  isPhoneNumberFound?: boolean;
  guestInfo?: GuestInfoType;
}

interface GuestInfoType {
  phoneNo: string;
  firstName?: string;
  lastName?: string;
  cityState?: string;
  address?: string;
  nameId?: number;
  addressId?: number;
  contactId?: number;
}

const defaultGuestInfo = {
  phoneNo: '',
  firstName: '',
  lastName: '',
  cityState: '',
  address: '',
};

const StyledTabs = withStyles({
  indicator: {
    display: 'flex',
    justifyContent: 'center',
    background: 'transparent',
    '& > span': {
      maxWidth: 200,
      width: '100%',
      backgroundColor: '#635ee7',
    },
  },
})((props: { [x: string]: any }) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />
));

const StyledTab = withStyles((theme) => ({
  root: {
    flex: 1,
    textTransform: 'none',
    color: '#142966',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    '&:focus': {
      opacity: 1,
    },
    maxWidth: 'inherit',
  },
}))((props: { [x: string]: any }) => <Tab disableRipple {...props} />);

const TabPanel = (props: any) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          p={1}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  container: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexDirection: 'column-reverse',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    alignSelf: 'center',
    justifyItems: 'stretch',
  },
  infoContainer: {
    flex: 1,
    marginRight: '12px',
    [theme.breakpoints.down('sm')]: {
      flex: 'none',
      marginRight: 'auto',
    },
  },
  idContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  textField: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  inputFile: {
    display: 'none',
    '&+ span': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1.2em',
    },
  },
  fileUpload: {
    border: '1px solid #ccc',
    display: 'inline-block',
    padding: '8px 16px',
    cursor: 'pointer',
    borderRadius: '4px',
    marginTop: '12px',
  },
  existingUserFooter: {
    marginTop: '48px',
    marginBottom: '24px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    display: 'flex',
  },
  newUserFooter: {
    display: 'flex',
    alignSelf: 'center',
    marginTop: '48px',
    marginBottom: '24px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
  },
}));

const GuestRegister = (props: GuestRegisterProps) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const [phoneLookedUp, setPhoneLookedUp] = useState(
    location?.state?.isPhoneLookedUp || false
  );
  const [phoneNoFound, setPhoneNoFound] = useState(
    location?.state?.isPhoneNumberFound || false
  );
  const [guestInfo, setGuestInfo] = useState<GuestInfoType>(
    location?.state?.guestInfo || defaultGuestInfo
  );

  const [editDisabled, setEditDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = React.useState(0);

  const [imageLoading, setImageLoading] = useState(false);
  const [frontId, setFrontId] = useState<any>();
  const [backId, setBackId] = useState<any>();

  const onPhoneLookupHandler = (err: any, res: any) => {
    console.log('PhoneLookup Error: ' + err);
    console.log('PhoneLookup: ' + JSON.stringify(res || {}));

    if (res.results.length > 0) {
      let result = res.results[0];
      setPhoneNoFound(true);
      setGuestInfo({
        phoneNo: res.inputPhoneNo,
        firstName: result.first_name,
        lastName: result.last_name,
        cityState: result.city_state,
        address: result.address,
        nameId: result.guest_name_id,
        addressId: result.guest_address_id,
        contactId: result.guest_contact_id,
      });

      setPhoneLookedUp(true);
      setImageLoading(true);
      if (isProduction) {
        let frontIdResolver = Storage.get(`front_${result.guest_name_id}.jpg`, {
          level: 'private',
          download: true,
          cacheControl: 'no-cache'
        });

        let backIdResolver = Storage.get(`back_${result.guest_name_id}.jpg`, {
          level: 'private',
          download: true,
          cacheControl: 'no-cache'
        });

        Promise.allSettled([frontIdResolver, backIdResolver]).then((values) => {
          setImageLoading(false);
          if (values[0].status === 'fulfilled') {
            setFrontId(URL.createObjectURL((values[0].value as any).Body));
          }

          if (values[1].status === 'fulfilled') {
            setBackId(URL.createObjectURL((values[1].value as any).Body));
          }
        });
      } else {
        let frontIdResolver = axios.get(
          `${PHOTO_ID_BASE_URI}?id=front_${result.guest_name_id}.jpg`,
          { responseType: 'blob' }
        );

        let backIdResolver = axios.get(
          `${PHOTO_ID_BASE_URI}?id=back_${result.guest_name_id}.jpg`,
          { responseType: 'blob' }
        );

        Promise.allSettled([frontIdResolver, backIdResolver]).then((values) => {
          setImageLoading(false);
          // console.log(values);
          if (values[0].status === 'fulfilled') {
            setFrontId(URL.createObjectURL((values[0].value as any).data));
          }

          if (values[1].status === 'fulfilled') {
            setBackId(URL.createObjectURL((values[1].value as any).data));
          }
        });
      }
    } else {
      setGuestInfo({ phoneNo: res.inputPhoneNo });
      setPhoneNoFound(false);
      setPhoneLookedUp(true);
    }
  };

  const handlePhotoIdChange = async (file?: any, front = false) => {
    if (file && guestInfo.nameId) {
      try {
        setLoading(true);
        // Upload the file to s3 with private access level.
        const uploadFileName = `${front ? 'front' : 'back'}_${
          guestInfo.nameId
        }.jpg`;

        if (isProduction) {
          await Storage.put(uploadFileName, file, {
            level: 'private',
            contentType: file.type,
          });

          // Retrieve the uploaded file to display
          const result: any = await Storage.get(uploadFileName, {
            level: 'private',
            download: true,
            cacheControl: 'no-cache'
          });

          if (front) {
            setFrontId(URL.createObjectURL(result.Body));
          } else {
            setBackId(URL.createObjectURL(result.Body));
          }
        } else {
          const formData = new FormData();
          formData.append('photoId', file, uploadFileName);

          axios
            .post(PHOTO_ID_BASE_URI, formData)
            .then((_) => {
              console.log('Guest Image Updated!');
              if (front) {
                setFrontId(URL.createObjectURL(file));
              } else {
                setBackId(URL.createObjectURL(file));
              }
            })
            .catch((err) => {
              console.log('Error: ', err);
              alert('Error updating guest image');
            })
            .finally(() => {
              setLoading(false);
            });
        }
      } catch (err) {
        alert('Error uploading photo ID!');
        console.log(err);
      } finally {
        setLoading(false);
      }
    } else if (guestInfo.nameId && guestInfo.nameId > 0) {
      alert('Photo ID selected is invalid!');
    } else {
      alert('Guest Info must be saved first!');
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    let firstName = guestInfo.firstName;
    let lastName = guestInfo.lastName;
    let address = guestInfo.address;
    let cityState = guestInfo.cityState;
    let phoneNo = guestInfo.phoneNo;

    if (
      firstName === undefined ||
      firstName.trim() === '' ||
      lastName === undefined ||
      lastName.trim() === '' ||
      phoneNo === undefined ||
      phoneNo.trim() === ''
    ) {
      window.alert(
        'Required info is missing. Please provide all the required info.'
      );
    } else {
      setLoading(true);

      axios
        .post('/api/addGuestInfo', {
          firstName,
          lastName,
          address,
          cityState,
          phoneNo,
        })
        .then((_) => {
          console.log('New Guest Added!');
        })
        .catch((err) => {
          console.log('Error: ', err);
          alert('Error adding guest info');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const resetGuestInfo = (e: any) => {
    e.preventDefault();
    setPhoneLookedUp(false);
    setPhoneNoFound(false);
    setGuestInfo(defaultGuestInfo);
    setFrontId(idPlaceholder);
    setBackId(idPlaceholder);
    setImageLoading(false);
    setLoading(false);
    setEditDisabled(true);
  };

  const FooterButtons = () => {
    if (!phoneNoFound) {
      return (
        <div className={classes.newUserFooter}>
          <NeutralButton
            style={{ marginLeft: '8px', marginRight: '8px' }}
            onClick={resetGuestInfo}
          >
            Reset
          </NeutralButton>
          <PrimaryButton
            style={{ marginLeft: '8px', marginRight: '8px' }}
            onClick={handleSubmit}
          >
            Save
          </PrimaryButton>
        </div>
      );
    } else {
      const editOrSave = editDisabled ? (
        <SecondaryButton
          style={{ marginLeft: '8px', marginRight: '8px' }}
          onClick={(e: any) => {
            e.preventDefault();
            setEditDisabled(false);
          }}
        >
          Edit
        </SecondaryButton>
      ) : (
        <PrimaryButton
          style={{ marginLeft: '8px', marginRight: '8px' }}
          onClick={(e: any) => {
            e.preventDefault();
            setLoading(true);
            axios
              .post('/api/updateGuestInfo', { guestInfo: guestInfo })
              .then((res: any) => res.data)
              .then((res: any) => {
                if (res.err || res.errno > 0) {
                  console.log('Failed to update!');
                } else {
                  setEditDisabled(true);
                }
              })
              .catch((err: any) => {
                if (err.response) {
                  alert(err.response.data.msg);
                } else if (err.request) {
                  alert('Error posting guest details.');
                } else {
                  alert('Unknown error occurred.');
                }
              })
              .finally(() => {
                setLoading(false);
              });
          }}
        >
          Save
        </PrimaryButton>
      );

      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div className={classes.existingUserFooter}>
            <NeutralButton
              style={{ marginRight: '12px' }}
              onClick={resetGuestInfo}
            >
              Reset
            </NeutralButton>
            {editOrSave}
            <NeutralButton
              style={{ marginLeft: '12px' }}
              onClick={(e) => {
                e.preventDefault();
                navigate('/checkIn', {
                  replace: true,
                  state: {
                    guestInfo: guestInfo,
                    isPhoneLookedUp: phoneLookedUp,
                    isPhoneNumberFound: phoneNoFound,
                  },
                });
              }}
            >
              Go To Check-In
            </NeutralButton>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else if (!phoneLookedUp) {
    return (
      <PhoneLookup
        phone={guestInfo.phoneNo || ''}
        onLookupHandler={onPhoneLookupHandler}
      />
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Guest Register</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.container}>
          <div className={classes.infoContainer}>
            <TextField
              className={classes.textField}
              label="First Name"
              id="firstName"
              margin="dense"
              fullWidth
              disabled={phoneNoFound && editDisabled}
              value={guestInfo.firstName || ''}
              onChange={(e) => {
                setGuestInfo({ ...guestInfo, firstName: e.target.value });
              }}
            />
            <TextField
              className={classes.textField}
              label="Last Name"
              id="lastName"
              margin="dense"
              fullWidth
              disabled={phoneNoFound && editDisabled}
              value={guestInfo.lastName || ''}
              onChange={(e) => {
                setGuestInfo({ ...guestInfo, lastName: e.target.value });
              }}
            />
            <TextField
              className={classes.textField}
              label="Address"
              id="address"
              margin="dense"
              fullWidth
              disabled={phoneNoFound && editDisabled}
              value={guestInfo.address || ''}
              onChange={(e) => {
                setGuestInfo({ ...guestInfo, address: e.target.value });
              }}
            />
            <TextField
              className={classes.textField}
              label="City, State"
              id="cityState"
              margin="dense"
              fullWidth
              disabled={phoneNoFound && editDisabled}
              value={guestInfo.cityState || ''}
              onChange={(e) => {
                setGuestInfo({ ...guestInfo, cityState: e.target.value });
              }}
            />
            <TextField
              className={classes.textField}
              label="Phone No"
              id="phoneNo"
              margin="dense"
              fullWidth
              disabled={phoneNoFound && editDisabled}
              value={guestInfo.phoneNo}
              onChange={(e) => {
                setGuestInfo({ ...guestInfo, phoneNo: e.target.value });
              }}
            />
          </div>
          <div className={classes.idContainer}>
            <StyledTabs
              value={value}
              onChange={(_: any, newValue: number) => setValue(newValue)}
              aria-label="styled tabs example"
            >
              <StyledTab label="Front" />
              <StyledTab label="Back" />
            </StyledTabs>
            <TabPanel value={value} index={0}>
              {imageLoading ? (
                <Loading />
              ) : (
                <>
                  <img
                    src={frontId || idPlaceholder}
                    style={{ maxWidth: '400px', aspectRatio: '1' }}
                    alt={'Front ID'}
                  />
                  <label className={classes.fileUpload}>
                    <input
                      type="file"
                      accept="image/jpg"
                      name={`front_${guestInfo.nameId}`}
                      disabled={guestInfo.nameId === undefined}
                      className={classes.inputFile}
                      onChange={(e: any) =>
                        handlePhotoIdChange(e.target.files[0], true)
                      }
                    />
                    <span>
                      <CloudUpload style={{ marginRight: '12px' }} /> Upload
                      Front
                    </span>
                  </label>
                </>
              )}
            </TabPanel>
            <TabPanel value={value} index={1}>
              {imageLoading ? (
                <Loading />
              ) : (
                <>
                  <img
                    src={backId || idPlaceholder}
                    style={{ maxWidth: '400px', aspectRatio: '1' }}
                    alt={'Back ID'}
                  />
                  <label className={classes.fileUpload}>
                    <input
                      type="file"
                      accept="image/jpg"
                      name={`back_${guestInfo.nameId}`}
                      disabled={guestInfo.nameId === undefined}
                      className={classes.inputFile}
                      onChange={(e: any) =>
                        handlePhotoIdChange(e.target.files[0])
                      }
                    />
                    <span>
                      <CloudUpload style={{ marginRight: '12px' }} /> Upload
                      Back
                    </span>
                  </label>
                </>
              )}
            </TabPanel>
          </div>
        </div>
        {FooterButtons()}
      </div>
    );
  }
};

export default GuestRegister;
